/* Branded presentation for native selectors; the original select owns form values. */
(() => {
  'use strict';
  const states = new WeakMap();
  const quantityStates = new WeakMap();
  let openControl = null;
  const quantitySelector = root => {
    if (quantityStates.has(root)) return;
    const select = root.querySelector('[data-tf-quantity-choice]');
    const input = root.querySelector('[data-tf-quantity-number]');
    const label = root.querySelector('[data-tf-quantity-label]');
    const otherLabel = root.querySelector('[data-tf-other-quantity-label]');
    if (!select || !input || !label || !otherLabel) return;
    const originalFor = label.htmlFor;
    const update = () => {
      const other = select.value === 'other';
      input.hidden = !other;
      otherLabel.hidden = !other;
      if (other) otherLabel.htmlFor = input.id;
      else otherLabel.removeAttribute('for');
      if (other) input.focus({ preventScroll:true });
      else input.value = select.value;
    };
    label.htmlFor = select.id;
    select.hidden = false;
    select.addEventListener('change', update);
    update();
    quantityStates.set(root, () => {
      select.removeEventListener('change', update);
      select.hidden = true;
      input.hidden = false;
      otherLabel.hidden = true;
      otherLabel.removeAttribute('for');
      label.htmlFor = originalFor;
      quantityStates.delete(root);
    });
  };
  const enhance = select => {
    if (states.has(select) || !select.id || select.disabled || !select.labels?.length || !select.options.length) return;
    const label = select.labels[0];
    const options = Array.from(select.options);
    const originalFor = label.htmlFor;
    const originalHidden = select.hidden;
    const originalLabelId = label.id;
    if (!label.id) label.id = `${select.id}-label`;
    const root = document.createElement('div');
    root.className = `tf-sort-control tf-custom-select${select.id === 'TfLanguage' ? ' tf-custom-select--language' : ''}`;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = `${select.id}-custom-trigger`;
    trigger.className = 'tf-sort-control__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    if (select.hasAttribute('aria-describedby')) trigger.setAttribute('aria-describedby', select.getAttribute('aria-describedby'));
    const valueLabel = document.createElement('span');
    valueLabel.id = `${select.id}-custom-value`;
    trigger.setAttribute('aria-labelledby', `${label.id} ${valueLabel.id}`);
    const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    arrow.setAttribute('viewBox', '0 0 20 20');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML = '<path d="m5 7 5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.5"/>';
    trigger.append(valueLabel, arrow);
    const list = document.createElement('ul');
    list.id = `${select.id}-custom-list`;
    list.className = 'tf-sort-control__list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-labelledby', label.id);
    list.tabIndex = -1;
    list.hidden = true;
    trigger.setAttribute('aria-controls', list.id);
    const nodes = options.map((option, index) => {
      const node = document.createElement('li');
      node.id = `${list.id}-${index}`;
      node.className = 'tf-sort-control__option';
      node.setAttribute('role', 'option');
      node.textContent = option.textContent.trim();
      if (option.disabled) node.setAttribute('aria-disabled', 'true');
      return node;
    });
    list.append(...nodes);
    root.append(trigger, list);
    select.after(root);
    let active = Math.max(0, select.selectedIndex), opened = false, prefix = '', prefixTimer;
    const enabled = index => index >= 0 && index < options.length && !options[index].disabled;
    const nearest = (index, step) => {
      for (let i = index; i >= 0 && i < options.length; i += step) if (enabled(i)) return i;
      return active;
    };
    const activate = index => {
      active = enabled(index) ? index : nearest(index, index < active ? -1 : 1);
      nodes.forEach((node, i) => node.toggleAttribute('data-tf-sort-active', i === active));
      list.setAttribute('aria-activedescendant', nodes[active].id);
      nodes[active].scrollIntoView({ block:'nearest', behavior:'auto' });
    };
    const sync = () => {
      const selected = Math.max(0, select.selectedIndex);
      trigger.disabled = select.disabled;
      valueLabel.textContent = options[selected]?.textContent.trim() || '';
      nodes.forEach((node, i) => {
        node.setAttribute('aria-selected', String(i === selected));
        if (options[i].disabled) node.setAttribute('aria-disabled', 'true');
        else node.removeAttribute('aria-disabled');
      });
    };
    const close = (returnFocus = false) => {
      opened = false;
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      list.removeAttribute('aria-activedescendant');
      nodes.forEach(node => node.removeAttribute('data-tf-sort-active'));
      prefix = '';
      window.clearTimeout(prefixTimer);
      if (openControl?.root === root) openControl = null;
      if (returnFocus && trigger.isConnected) trigger.focus({ preventScroll:true });
    };
    const open = index => {
      if (!root.isConnected || select.disabled) return;
      openControl?.close();
      opened = true;
      openControl = { root, close };
      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      activate(index ?? select.selectedIndex);
      list.focus({ preventScroll:true });
    };
    const commit = index => {
      if (!enabled(index)) return;
      const before = select.value;
      select.selectedIndex = index;
      sync();
      close(true);
      if (select.value !== before) select.dispatchEvent(new Event('change', { bubbles:true }));
    };
    const onTriggerClick = () => opened ? close(true) : open();
    const onTriggerKey = event => {
      if (!['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
      event.preventDefault();
      open(event.key === 'Home' ? nearest(0, 1) : event.key === 'End' ? nearest(options.length - 1, -1) : undefined);
    };
    const onListKey = event => {
      if (['ArrowDown','ArrowUp','Home','End'].includes(event.key)) {
        event.preventDefault();
        activate(event.key === 'Home' ? nearest(0, 1) : event.key === 'End' ? nearest(options.length - 1, -1) : nearest(active + (event.key === 'ArrowDown' ? 1 : -1), event.key === 'ArrowDown' ? 1 : -1));
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault(); commit(active);
      } else if (event.key === 'Escape') {
        event.preventDefault(); close(true);
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const char = event.key.toLocaleLowerCase();
        prefix = prefix && Array.from(prefix).every(value => value === char) ? char : prefix + char;
        window.clearTimeout(prefixTimer);
        prefixTimer = window.setTimeout(() => { prefix = ''; }, 700);
        const matches = options.map((option, index) => !option.disabled && option.textContent.trim().toLocaleLowerCase().startsWith(prefix) ? index : -1).filter(index => index >= 0);
        if (matches.length) activate((prefix.length === 1 ? matches.find(index => index > active) : matches[0]) ?? matches[0]);
      }
    };
    const onListClick = event => {
      const node = event.target.closest('[role="option"]');
      const index = nodes.indexOf(node);
      if (index >= 0) commit(index);
    };
    const onFocusOut = () => queueMicrotask(() => { if (opened && !root.contains(document.activeElement)) close(); });
    const onChange = () => { sync(); close(); };
    trigger.addEventListener('click', onTriggerClick);
    trigger.addEventListener('keydown', onTriggerKey);
    list.addEventListener('keydown', onListKey);
    list.addEventListener('click', onListClick);
    root.addEventListener('focusout', onFocusOut);
    select.addEventListener('change', onChange);
    select.hidden = true;
    label.htmlFor = trigger.id;
    sync();
    states.set(select, () => {
      close();
      select.hidden = originalHidden;
      label.htmlFor = originalFor;
      label.id = originalLabelId;
      root.remove();
      states.delete(select);
    });
  };
  const selector = '#TfLanguage,#TfContactSubject,#TfContactProduct,[data-tf-quantity-choice]';
  const bind = scope => {
    scope.querySelectorAll?.('[data-tf-chain-quantity]').forEach(quantitySelector);
    scope.querySelectorAll?.(selector).forEach(select => {
      try { enhance(select); } catch { /* The native selector remains available. */ }
    });
  };
  bind(document);
  document.addEventListener('shopify:section:load', event => bind(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll?.(selector).forEach(select => states.get(select)?.());
    event.target.querySelectorAll?.('[data-tf-chain-quantity]').forEach(root => quantityStates.get(root)?.());
  });
  document.addEventListener('pointerdown', event => { if (openControl && !openControl.root.contains(event.target)) openControl.close(); });
})();
